import type { Emplacement, Palier } from '@whatremains/shared'
import { blocage, PALIER_COULEUR } from '@whatremains/shared'

/**
 * Le nom d'une pièce, et la seule chose qu'on veut savoir avant de le lire :
 * est-ce qu'on peut s'en servir.
 *
 * ## Pourquoi un composant, et pas dix conditions
 *
 * Enzo : « passer en titre rouge quand on ne peut pas équiper. Ça aidera
 * visuellement à voir directement quand un item peut être équipé ou pas.
 * N'oublie aucun endroit. Parce que parfois on fait des patchs et il reste des
 * endroits ou onglets où ce n'est pas pris en compte. »
 *
 * Sa méfiance est fondée, et elle décrit exactement ce qui serait arrivé : dix
 * endroits du client peignent un nom de pièce — la poupée, l'atelier, les deux
 * listes du négoce, le marché, le butin de combat, le sac de donjon, le butin
 * nommé, la confrérie. Dix conditions recopiées auraient divergé, et le
 * onzième écran, écrit dans six mois, ne l'aurait jamais eue.
 *
 * D'où la forme : la RÈGLE est dans `shared` (`blocage`), la PEINTURE est
 * ici, et chaque écran ne fait qu'appeler. Un nouvel écran qui affiche une
 * pièce l'a par construction.
 *
 * ## Ce que le rouge dit, et ce qu'il ne dit pas
 *
 * Il dit « tu ne peux pas t'en servir », pas « c'est mauvais ». Une pièce hors
 * de portée se garde et se revend, ce que le reste de l'écran continue de
 * proposer. Et un matériau n'est jamais rouge : il ne se porte ni ne se boit,
 * donc rien ne le bloque, et le peindre dirait faux.
 */

/** Ce que la couleur remplace quand la pièce est hors d'usage. */
const BLOQUE = 'var(--r6)'

export interface PieceNommee {
  nom: string
  palier: Palier
  categorie: 'equipement' | 'materiau' | 'consommable'
  niveauRequis: number
  /** Le verdict du serveur, quand la forme le porte. Il prime sur `niveau`. */
  horsDePortee?: boolean
  /** Absent des formes courtes. Sans lui, on ne conclut pas au blocage de classe. */
  emplacementsPossibles?: readonly Emplacement[]
}

export function NomObjet({
  o,
  niveau,
  balise: Balise = 'b',
  className,
  suffixe,
}: {
  o: PieceNommee
  /**
   * Le niveau du personnage, pour les formes qui ne portent pas le verdict du
   * serveur. Facultatif à dessein : la plupart des écrans montrent des pièces
   * sans savoir à qui, et c'est `horsDePortee` qui répond pour eux.
   */
  niveau?: number
  balise?: 'b' | 'span'
  className?: string
  /** Ce qui se colle au nom sans être le nom : « ×3 », un cran d'amélioration. */
  suffixe?: React.ReactNode
}) {
  const bloque = blocage(o, niveau)
  return (
    <Balise
      className={className}
      style={{ color: bloque ? BLOQUE : (PALIER_COULEUR[o.palier] ?? undefined) }}
      title={
        bloque === 'niveau'
          ? `Il te faut le niveau ${o.niveauRequis}.`
          : bloque === 'classe'
            ? 'Ta classe ne manie pas ça.'
            : undefined
      }
    >
      {o.nom}
      {suffixe}
    </Balise>
  )
}

/**
 * Ce qu'une pièce FAIT, en vert, partout où elle paraît.
 *
 * Enzo : « sur les items qui ont une capacité, comme les items qui heal, il
 * faudrait mettre en vert leur effet, que dans la description on puisse plus
 * facilement lire les choses. »
 *
 * La couleur était déjà la bonne aux trois endroits qui l'affichaient — la
 * poupée, la fiche, l'échoppe. Le défaut n'était donc pas la teinte mais
 * l'ABSENCE : quatre des formes de pièce du serveur ne transportaient même pas
 * l'effet, si bien qu'un butin, un rachat, une pièce forgeable et un dépôt ne
 * pouvaient rien en dire. Une même chose décrite à cinq endroits finit décrite
 * de cinq façons.
 */
export function EffetObjet({ effet }: { effet: string | null }) {
  return effet ? <span className="objet-effet">{effet}</span> : null
}
